import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary. Catches render-time errors anywhere in the tree
 * and shows a recoverable fallback instead of a blank page.
 */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Later phase: forward to a server-side logging endpoint.
    console.error("Unhandled UI error:", error, info.componentStack);
  }

  private readonly handleReload = (): void => {
    this.setState({ error: null });
    window.location.assign("/");
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="centered-panel">
        <div className="centered-panel-inner">
          <div className="centered-code">:(</div>
          <h1 className="centered-title">Something went wrong</h1>
          <p className="centered-text">
            An unexpected error occurred while rendering the page.
          </p>
          <pre className="error-detail">{error.message}</pre>
          <button
            type="button"
            className="btn btn-primary"
            onClick={this.handleReload}
          >
            Reload application
          </button>
        </div>
      </div>
    );
  }
}
